<template>
  <div>
    <service-view-statistics :service="service" />

    <div class="app-tour-service-view-details">
      <b-row class="match-height">
        <b-col>
          <b-card
            class="shadow-none"
            :title="$t('services.form.base-information.title')"
          >

            <b-row
              v-if="service.shortDescription"
              class="mb-1"
            >
              <b-col class="mb-50">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.base-information.short-description') }}
                </h6>
                {{ service.shortDescription }}
              </b-col>
            </b-row>

            <b-row
              class="mb-1"
            >

              <b-col cols="6">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.base-information.price-gross') }}
                </h6>
                {{ service.priceGross }} zł {{ $t(`shared.price-types.${service.priceType}`).toLowerCase() }}
              </b-col>

              <b-col cols="6">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.base-information.tax-percent') }}
                </h6>
                {{ service.taxPercent }}%
              </b-col>
            </b-row>

            <b-row
              class="mb-1"
            >
              <b-col cols="6">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.base-information.duration') }}
                </h6>
                {{ toHours(service.duration) }}h {{ toMinutes(service.duration) }}min.
              </b-col>

              <b-col cols="6">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.is-public') }}
                </h6>
                {{ service.public ? $t('shared.yes') : $t('shared.no') }}
              </b-col>

            </b-row>

          </b-card>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <b-col>
          <b-card
            class="shadow-none"
            :title="$t('services.form.default-settings.title')"
          >

            <b-row
              class="mb-1"
            >

              <b-col cols="6">
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('shared.service-location.title') }}
                </h6>
                <span
                  v-for="(location, key) in service.serviceLocation"
                  :key="key"
                >
                  {{ $t(`shared.service-location.${location}`) }},
                </span>
              </b-col>
            </b-row>

            <b-row
              class="mb-1"
            >

              <b-col
                v-if="service.additionalTimeBefore"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.default-settings.additional-time-before') }}
                </h6>
                {{ toHours(service.additionalTimeBefore) }}h {{ toMinutes(service.additionalTimeBefore) }}min.
              </b-col>

              <b-col
                v-if="service.additionalTimeAfter"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.default-settings.additional-time-after') }}
                </h6>
                {{ toHours(service.additionalTimeAfter) }}h {{ toMinutes(service.additionalTimeAfter) }}min.
              </b-col>
            </b-row>

          </b-card>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <b-col>
          <b-card
            class="shadow-none"
            :title="$t('services.form.public-reservation.title')"
          >

            <h5 class="mt-n1 mb-2">
              <span v-if="service.public">
                <feather-icon icon="CheckIcon" />
                {{ $t('services.form.public-reservation.is-public') }}
              </span>
              <span v-else>
                <feather-icon icon="XIcon" />
                {{ $t('services.form.public-reservation.is-not-public') }}
              </span>
            </h5>

            <b-row
              class="mb-1"
            >
              <b-col
                cols="12"
                md="6"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('shared.self-reservation-types.title') }}
                </h6>
                {{ $t(`shared.self-reservation-types.${service.selfReservationType}`) }}
              </b-col>

              <b-col
                cols="12"
                md="6"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.participant-limit') }}
                </h6>
                {{ service.participantLimit }}
              </b-col>

              <b-col
                v-if="service.reservationCutoffDate"
                cols="12"
                md="6"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.reservation-cutoff-date') }}
                </h6>
                {{ transformDateToHumanReadablePretty(service.reservationCutoffDate) }}
              </b-col>

              <b-col
                v-if="service.selfReservationType === 'freely' && service.serviceWeekdays && service.serviceWeekdays.length"

                cols="12"
                md="6"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.service-weekdays') }}
                </h6>
                {{ joinTranslatedServiceWeekdays() }}
              </b-col>

              <b-col
                v-if="service.serviceTimeFrom && service.serviceTimeTo"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.service-time-range') }}
                </h6>
                {{ service.serviceTimeFrom }} - {{ service.serviceTimeTo }}
              </b-col>

              <b-col
                v-else-if="service.serviceTimeFrom"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.service-time-from') }}
                </h6>
                {{ service.serviceTimeFrom }}
              </b-col>

              <b-col
                v-else-if="service.serviceTimeTo"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.service-time-to') }}
                </h6>
                {{ service.serviceTimeTo }}
              </b-col>

              <b-col
                v-if="service.selfReservationType === 'freely' && service.reservationInterval"

                cols="12"
                md="6"
                class="mb-50"
              >
                <h6 class="text-muted text-uppercase font-small-3">
                  {{ $t('services.form.public-reservation.reservation-interval') }}
                </h6>
                {{ toHours(service.reservationInterval) }}h {{ toMinutes(service.reservationInterval) }}min.
              </b-col>
            </b-row>

          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<script>
import { ref } from '@vue/composition-api'
import { BCard, BCol, BRow } from 'bootstrap-vue'
import ServiceViewStatistics from '@/views/organization/services/ServiceViewStatistics'
import { transformDateToHumanReadablePretty } from '@/helpers/dateTimeHelper'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    ServiceViewStatistics,
  },
  props: {
    service: {
      type: Object,
      required: true,
    },
  },
  methods: {
    transformDateToHumanReadablePretty,
    toHours(val) {
      return Math.floor(val / 60).toString()
    },
    toMinutes(val) {
      return (val - Math.floor(val / 60) * 60).toString()
    },
    getDayName(dayValue) {
      const dayMap = {
        1: 'monday',
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
        6: 'saturday',
        7: 'sunday',
      }
      return dayMap[dayValue] || dayValue
    },
    joinTranslatedServiceWeekdays() {
      const translatedWeekdays = []
      this.service.serviceWeekdays.forEach(dayValue => {
        translatedWeekdays.push(this.$t(`shared.days.named.${this.getDayName(dayValue)}`))
      })

      return translatedWeekdays.join(', ')
    },
  },
  setup() {
    const updating = ref(false)
    const setUpdating = newUpdating => { updating.value = newUpdating }

    return {
      updating,
      setUpdating,
    }
  },
}
</script>

<style scoped>
.card {margin-bottom: 1rem;}
</style>
